import * as React from 'react'
import type { Row } from '@tanstack/react-table'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import type { DataTableFilterField } from '@/components/data-table/types'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { createSchema, field } from '@/lib/store/schema'
import { SECTION_ORDER } from '@/features/permissions/capabilities'
import {
  baseline,
  changedCapabilityIds,
  expandablePageKeys,
  expandPageRows,
  nestPageRows,
  filterCapabilities,
  rolesLockedByPhi,
  rowsForScope,
  rowsWithSelection,
} from '../matrix'
import {
  usePermissionGrantsQuery,
  useSavePermissionGrantsMutation,
} from '@/features/permissions/hooks'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { buildGrants, PRODUCT_WIDE_SCOPE, saveConfirmCopy } from '../grants'
import { listedOrgs } from '../orgScopes'
import {
  ALL_ROLE_KEYS,
  type Capability,
  type MatrixRow,
  type PermissionSelection,
  type RoleKey,
} from '../types'
import { buildColumns } from './matrixColumns'

const TABLE_ID = 'permissions-matrix'

// Filter keys match the column ids the rail targets.
const permissionsSchema = createSchema({
  capability: field.string().default(null),
  section: field.array(field.string()).delimiter(',').default([]),
  phi: field.array(field.string()).delimiter(',').default([]),
})

const SECTION_OPTIONS = SECTION_ORDER.map((s) => ({ label: s, value: s }))
const PHI_OPTIONS = [
  { label: 'PHI', value: 'phi' },
  { label: 'Non-PHI', value: 'non-phi' },
]

function PermissionsMatrixInner() {
  // The grants table is the whole picture: a capability with no row there is
  // ticked for nobody here, because it permits nobody on the server.
  const grantsQuery = usePermissionGrantsQuery()
  const stored = grantsQuery.data?.grants
  const scope = grantsQuery.data?.scope ?? PRODUCT_WIDE_SCOPE
  // Undefined until the first response: a row is not editable until the server
  // has said it guards it, so a slow answer shows read-only rather than a
  // control that would save nothing.
  const enforced = React.useMemo(
    () =>
      grantsQuery.data?.enforced
        ? new Set(grantsQuery.data.enforced)
        : undefined,
    [grantsQuery.data?.enforced]
  )
  const editingProductWide = scope === PRODUCT_WIDE_SCOPE

  // The server's answer, derived rather than copied into state. Copying it
  // meant a refetch either clobbered edits in flight or, if it skipped them,
  // left `saved` describing a snapshot the editor no longer matched — so rows
  // nobody touched looked changed and would be written back as defaults.
  const saved = React.useMemo<PermissionSelection>(() => {
    const merged = baseline()
    for (const [id, grant] of Object.entries(stored ?? {})) {
      merged[id] = grant.roles
    }
    return merged
  }, [stored])

  // Which orgs hold each capability, as the server last reported it. Only the
  // Careforce scope carries this; a customer's matrix has no such column.
  const savedOrgScopes = React.useMemo<
    Record<string, string[]> | undefined
  >(() => {
    if (!editingProductWide || !stored) return undefined
    return Object.fromEntries(
      Object.entries(stored).map(([id, grant]) => [id, grant.orgs ?? []])
    )
  }, [editingProductWide, stored])

  // Every org that could hold a capability, Careforce first. It arrives with
  // the grants, read from the organization registry: a configuration row can
  // outlive the org it names, and an org the column offers has to be one a
  // capability can actually be given to.
  const allOrgs = React.useMemo(
    () => listedOrgs(grantsQuery.data?.orgs ?? [], []),
    [grantsQuery.data?.orgs]
  )

  // Only what the operator actually changed. A refetch updates every other row
  // on its own, because the rows are derived from `saved`.
  const [edits, setEdits] = React.useState<PermissionSelection>({})
  const [orgEdits, setOrgEdits] = React.useState<Record<string, string[]>>({})

  const selection = React.useMemo<PermissionSelection>(
    () => ({ ...saved, ...edits }),
    [saved, edits]
  )

  const orgScopes = React.useMemo<Record<string, string[]> | undefined>(
    () => (savedOrgScopes ? { ...savedOrgScopes, ...orgEdits } : undefined),
    [savedOrgScopes, orgEdits]
  )

  // Whether the column exists, not what is in it: the live scope rides on the
  // rows, so editing it must not rebuild the column defs.
  const showOrgs = orgScopes !== undefined

  const handleOrgScopeChange = React.useCallback(
    (id: string, next: string[]) => {
      setOrgEdits((current) => ({ ...current, [id]: next }))
    },
    []
  )

  /**
   * A page row's dropdown moves every row on the page to the same scope.
   *
   * The header shows the union of what its rows reach, so it can never say
   * less than one of them; setting it makes them all agree.
   */
  const handlePageOrgScopeChange = React.useCallback(
    (ids: string[], next: string[]) => {
      setOrgEdits((current) => {
        const edited = { ...current }
        for (const id of ids) edited[id] = next
        return edited
      })
    },
    []
  )

  // A row counts as changed when its orgs moved, even if nobody touched a
  // checkbox — the save has to carry it either way.
  const changedOrgIds = React.useMemo(() => {
    if (!savedOrgScopes) return []
    return Object.keys(orgEdits).filter((id) => {
      const before = savedOrgScopes[id] ?? []
      const after = orgEdits[id] ?? []
      return (
        before.length !== after.length ||
        before.some((slug, i) => slug !== after[i])
      )
    })
  }, [savedOrgScopes, orgEdits])

  // Every row reads as "nobody" when the load fails, which is indistinguishable
  // from a table that genuinely grants nothing. Saving from here would write
  // that emptiness over whatever is stored, so the button is blocked too.
  React.useEffect(() => {
    if (grantsQuery.isError) {
      toast.error(
        'Could not load saved permissions. Every row is showing empty — that is the failure, not the stored state.',
        { id: 'permissions-load-failed', duration: 8000 }
      )
    }
  }, [grantsQuery.isError])

  const saveGrants = useSavePermissionGrantsMutation()
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  const changedIds = React.useMemo(
    () => changedCapabilityIds(selection, saved),
    [selection, saved]
  )
  // The stored state is unknown until the first response lands, so a save now
  // would write empty rows over whatever the table holds.
  const baselineUnknown = grantsQuery.isLoading || grantsQuery.isError
  const dirtyIds = React.useMemo(
    () => [...new Set([...changedIds, ...changedOrgIds])],
    [changedIds, changedOrgIds]
  )
  const isDirty = dirtyIds.length > 0

  const handleRolesChange = React.useCallback((id: string, next: RoleKey[]) => {
    setEdits((current) => ({ ...current, [id]: next }))
  }, [])

  /**
   * One role, ticked or unticked on a page row, applied to the whole page.
   *
   * The boxes on a page row stand for every row beneath it, so exactly one
   * role differs between what they showed and what the operator left. That
   * role is granted to all of them, or taken from all of them.
   *
   * A row the PHI rule closes to that role is skipped rather than written:
   * the server refuses such a grant, and one of them would fail the whole
   * save — so ticking Member across a page holding one PHI row would save
   * nothing at all.
   */
  const handlePageRolesChange = React.useCallback(
    (ids: string[], before: RoleKey[], next: RoleKey[]) => {
      const added = next.filter((role) => !before.includes(role))
      const removed = before.filter((role) => !next.includes(role))
      setEdits((current) => {
        const edited = { ...current }
        for (const id of ids) {
          const held = new Set(edited[id] ?? selection[id] ?? [])
          const locked = rolesLockedByPhi(id)
          for (const role of added) {
            if (!locked.includes(role)) held.add(role)
          }
          for (const role of removed) held.delete(role)
          edited[id] = ALL_ROLE_KEYS.filter((role) => held.has(role))
        }
        return edited
      })
    },
    [selection]
  )

  // Pages start closed: the governing rows alone are the shape of the product,
  // and what each one contains is a question the operator asks page by page.
  const [openPages, setOpenPages] = React.useState<ReadonlySet<string>>(
    () => new Set()
  )
  const handlePageToggle = React.useCallback((pageKey: string) => {
    setOpenPages((open) => {
      const next = new Set(open)
      if (!next.delete(pageKey)) next.add(pageKey)
      return next
    })
  }, [])

  const columns = React.useMemo(
    () =>
      buildColumns({
        onRolesChange: handleRolesChange,
        onPageRolesChange: handlePageRolesChange,
        onPageToggle: handlePageToggle,
        showOrgs,
        onOrgScopeChange: handleOrgScopeChange,
        onPageOrgScopeChange: handlePageOrgScopeChange,
        allOrgs,
      }),
    [
      handleRolesChange,
      handlePageRolesChange,
      handlePageToggle,
      showOrgs,
      handleOrgScopeChange,
      handlePageOrgScopeChange,
      allOrgs,
    ]
  )

  // Rows carry the live selection so the column defs stay stable across edits.
  // What this org holds: the capabilities the server returned a grant for.
  // Undefined for Careforce, which sees the whole catalogue because the org
  // column is how a capability gets its first row.
  const held = React.useMemo<ReadonlySet<string> | undefined>(
    () => (editingProductWide ? undefined : new Set(Object.keys(stored ?? {}))),
    [editingProductWide, stored]
  )

  const rows = React.useMemo<MatrixRow[]>(
    () =>
      // Listed under the read that opens their page, and marked when nobody
      // can open it.
      nestPageRows(
        rowsForScope(rowsWithSelection(selection, orgScopes), held),
        enforced
      ),
    [selection, orgScopes, held, enforced]
  )

  const expandableKeys = React.useMemo(() => expandablePageKeys(rows), [rows])
  const allOpen =
    expandableKeys.length > 0 && expandableKeys.every((k) => openPages.has(k))
  const toggleAllPages = React.useCallback(
    () => setOpenPages(allOpen ? new Set() : new Set(expandableKeys)),
    [allOpen, expandableKeys]
  )

  // Client-side data, but filtering is apply-on-click like the server tables:
  // filter by the APPLIED store filters and pass `manualFiltering` so TanStack
  // doesn't re-filter the draft on every keystroke.
  const appliedFilters = useFilterState()

  const capabilityFilter =
    typeof appliedFilters?.capability === 'string'
      ? appliedFilters.capability
      : undefined
  const sectionFilter = Array.isArray(appliedFilters?.section)
    ? (appliedFilters.section as string[])
    : undefined
  const phiFilter = Array.isArray(appliedFilters?.phi)
    ? (appliedFilters.phi as string[])
    : undefined

  // A search reaches into closed pages. Leaving them closed would answer a
  // search for "import" with nothing, when the row is sitting one page down.
  const searching = Boolean(
    capabilityFilter || sectionFilter?.length || phiFilter?.length
  )
  const shownPages = React.useMemo<ReadonlySet<string>>(
    () => (searching ? new Set(expandableKeys) : openPages),
    [searching, expandableKeys, openPages]
  )

  const filteredRows = React.useMemo<MatrixRow[]>(
    () =>
      filterCapabilities(expandPageRows(rows, shownPages), {
        capability: capabilityFilter,
        sections: sectionFilter,
        phi: phiFilter,
      }),
    [rows, shownPages, capabilityFilter, sectionFilter, phiFilter]
  )

  const filterFields = React.useMemo<DataTableFilterField<Capability>[]>(
    () => [
      {
        label: 'Capability',
        value: 'capability',
        type: 'input',
        defaultOpen: true,
      },
      {
        label: 'Section',
        value: 'section',
        type: 'checkbox',
        defaultOpen: true,
        options: SECTION_OPTIONS,
      },
      {
        label: 'PHI',
        // Filters the `phi` column, whose accessor maps the boolean to a token.
        value: 'phi' as keyof Capability,
        type: 'checkbox',
        defaultOpen: true,
        options: PHI_OPTIONS,
      },
    ],
    []
  )

  // The inventory is a static list — nothing to page through or refetch.
  const noopFetchNextPage = React.useCallback(() => Promise.resolve(), [])
  const noopRefetch = React.useCallback(() => undefined, [])
  const getRowId = React.useCallback((row: MatrixRow) => row.id, [])

  // Nested rows are tinted so an opened page reads as one block rather than as
  // more rows in the list. `bg-secondary` rather than `bg-muted`: both are
  // theme tokens that flip for dark, but muted resolves to slate-2, which is
  // within a couple of values of the table's own white.
  const getRowClassName = React.useCallback(
    (row: Row<MatrixRow>) => (row.original.dependent ? 'bg-secondary' : ''),
    []
  )

  const handleSave = React.useCallback(async () => {
    // The ticked roles are the whole grant: the server stores them as sent.
    const payload = buildGrants(
      selection,
      changedIds,
      Object.fromEntries(changedOrgIds.map((id) => [id, orgEdits[id] ?? []]))
    )
    try {
      await saveGrants.mutateAsync(payload)
      toast.success(
        `Saved ${dirtyIds.length} ${dirtyIds.length === 1 ? 'change' : 'changes'}.`
      )
      setEdits({})
      setOrgEdits({})
      setConfirmOpen(false)
    } catch (error) {
      // Leave the dialog open so there is something to retry from.
      toast.error(
        error instanceof Error ? error.message : 'Failed to save permissions.'
      )
    }
  }, [changedIds, changedOrgIds, dirtyIds, orgEdits, selection, saveGrants])

  const confirmCopy = saveConfirmCopy(scope, dirtyIds.length)

  const clearEdits = React.useCallback(() => {
    setEdits({})
    setOrgEdits({})
  }, [])

  const ToolbarActions = React.useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={expandableKeys.length === 0}
          onClick={toggleAllPages}>
          {allOpen ? 'Collapse all' : 'Expand all'}
        </Button>
        <span className="text-xs text-muted-foreground">
          {editingProductWide
            ? 'Editing all organizations'
            : `Editing ${scope} only`}
        </span>
        {isDirty && (
          <span className="text-xs text-muted-foreground">
            {dirtyIds.length} unsaved{' '}
            {dirtyIds.length === 1 ? 'change' : 'changes'}
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={!isDirty || saveGrants.isPending}
          onClick={clearEdits}>
          Cancel
        </Button>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              // Blocked while the grants failed to load: every row reads as
              // empty, so the diff would be computed against a state the
              // operator never saw, and one tick would save that emptiness
              // over every other row's stored grant.
              disabled={!isDirty || saveGrants.isPending || baselineUnknown}
              title={
                baselineUnknown
                  ? 'Saved permissions are not loaded yet, so changes cannot be applied safely.'
                  : undefined
              }>
              {saveGrants.isPending ? 'Saving…' : 'Save'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent size="sm">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmCopy.title}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmCopy.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {/* The ids themselves, so the operator can check what they are
                about to apply rather than trusting a count. */}
            <ul className="max-h-48 overflow-y-auto text-xs text-muted-foreground">
              {dirtyIds.map((id) => (
                <li key={id} className="font-mono">
                  {id}
                </li>
              ))}
            </ul>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saveGrants.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={saveGrants.isPending}
                onClick={(e) => {
                  // Radix closes the dialog synchronously on click; prevent
                  // that so a failed save leaves it open to retry from.
                  e.preventDefault()
                  void handleSave()
                }}>
                Apply
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    ),
    [
      isDirty,
      dirtyIds,
      confirmCopy,
      editingProductWide,
      scope,
      clearEdits,
      handleSave,
      saveGrants.isPending,
      baselineUnknown,
      confirmOpen,
      allOpen,
      expandableKeys,
      toggleAllPages,
    ]
  )

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <DataTableInfinite<MatrixRow, unknown>
        tableId={TABLE_ID}
        columns={columns}
        data={filteredRows}
        filterFields={filterFields}
        manualFiltering
        enableRowDetails={false}
        // Without this the table reads as 0, which makes every row test as
        // newly arrived and start the 15s highlight fade. The fade animates
        // `background-color`, so a row opened under a page sat green-to-white
        // for the whole animation before the tint below could show.
        dataUpdatedAt={grantsQuery.dataUpdatedAt}
        isLoading={grantsQuery.isLoading}
        isFetching={grantsQuery.isFetching}
        hasNextPage={false}
        fetchNextPage={noopFetchNextPage}
        refetch={noopRefetch}
        totalRows={filteredRows.length}
        totalRowsFetched={filteredRows.length}
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        toolbarActions={ToolbarActions}
      />
    </div>
  )
}

export function PermissionsMatrixPage() {
  const adapter = useNuqsAdapter(permissionsSchema.definition, {
    id: TABLE_ID,
    history: 'replace',
    // Namespace the filter params so `section`/`phi` don't collide with other
    // tables sharing the query string.
    namespaceUrlKeys: true,
  })

  return (
    <DataTableStoreProvider adapter={adapter}>
      <PermissionsMatrixInner />
    </DataTableStoreProvider>
  )
}
